import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Space, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { WEBSITE_CONTENT_ADMIN_LIST } from '../graphql/queries';
import { BATCH_DELETE_WEBSITE_CONTENT, BATCH_TAKE_WEBSITE_CONTENT_OFFLINE } from '../graphql/mutations';
import {
    WEBSITE_CONTENT_TYPES,
    WebsiteAdminList,
    WebsiteAdminListItem,
    WebsiteBatchResult,
    WebsiteContentAdminSort,
    WebsiteContentStatus,
    WebsiteContentType,
} from '../graphql/types';
import { describeScheduleFailure, resolveWebsiteContentError } from '../utils/errorMessage';
import {
    formatBeijingDateTime,
    formatLocalDateTime,
    formatPacificDate,
    formatPacificDateTime,
    normalizeSearchKeyword,
} from '../utils/format';
import ContentStatusTag from '../components/ContentStatusTag';
import ContentCover from '../components/ContentCover';
import ContentRowActions from './ContentRowActions';
import { rememberListSearch } from './listSearch';

const PAGE_SIZE = 20;
const MAX_KEYWORD_LENGTH = 100;
const ALL_TAB = 'ALL';
/** 页签顺序按原型：全部 / 已发布 / 待发布 / 草稿 / 已下架 */
const TAB_STATUSES: WebsiteContentStatus[] = ['PUBLISHED', 'SCHEDULED', 'DRAFT', 'OFFLINE'];

const parseStatus = (value: string | null): WebsiteContentStatus | null =>
    TAB_STATUSES.includes(value as WebsiteContentStatus) ? (value as WebsiteContentStatus) : null;
const parseType = (value: string | null): WebsiteContentType | null =>
    WEBSITE_CONTENT_TYPES.includes(value as WebsiteContentType) ? (value as WebsiteContentType) : null;
const parsePage = (value: string | null): number => Math.max(1, Number.parseInt(value ?? '', 10) || 1);

/**
 * 内容列表（T-A03）：状态页签带数量；搜索标题或短码（可粘贴官网链接）与类型筛选；
 * 状态、类型、关键词、排序、页码写进地址，参数名与接口一致，返回列表时保留位置。
 */
const WebsiteContentListPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const status = parseStatus(searchParams.get('status'));
    const contentType = parseType(searchParams.get('contentType'));
    const keyword = searchParams.get('keyword') ?? '';
    const sortBy: WebsiteContentAdminSort = searchParams.get('sortBy') === 'PUBLISH_TIME' ? 'PUBLISH_TIME' : 'UPDATED_AT';
    const page = parsePage(searchParams.get('page'));
    const [keywordInput, setKeywordInput] = useState(keyword);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useEffect(() => setKeywordInput(keyword), [keyword]);
    useEffect(() => rememberListSearch(location.search), [location.search]);
    useEffect(() => setSelectedIds([]), [status, contentType, keyword, sortBy, page]);

    const { data, previousData, loading, error, refetch } = useQuery<{ websiteContentAdminList: WebsiteAdminList }>(
        WEBSITE_CONTENT_ADMIN_LIST,
        {
            variables: { status, contentType, keyword: keyword || null, sortBy, page, pageSize: PAGE_SIZE, lang: 'EN' },
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
        },
    );
    const list = (data ?? previousData)?.websiteContentAdminList;

    useEffect(() => {
        if (!error) return;
        const text = resolveWebsiteContentError(error, t);
        if (text) message.error(text);
    }, [error, t]);

    const [batchOffline, { loading: offlining }] = useMutation<{ batchTakeWebsiteContentOffline: WebsiteBatchResult }>(
        BATCH_TAKE_WEBSITE_CONTENT_OFFLINE,
    );
    const [batchDelete, { loading: deleting }] = useMutation<{ batchDeleteWebsiteContent: WebsiteBatchResult }>(
        BATCH_DELETE_WEBSITE_CONTENT,
    );

    const updateParams = (patch: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
        });
        if (!('page' in patch)) next.delete('page');
        setSearchParams(next);
    };

    const countOf = (target: WebsiteContentStatus) =>
        list?.statusCounts.find((item) => item.status === target)?.count ?? 0;
    const totalCount = TAB_STATUSES.reduce((sum, item) => sum + countOf(item), 0);

    const titleById = useMemo(() => {
        const map = new Map<string, string>();
        list?.items.forEach((item) => map.set(item.content.id, item.title || t('websiteContent.list.untitled')));
        return map;
    }, [list, t]);

    const showBatchResult = (result: WebsiteBatchResult, kind: 'offline' | 'delete') => {
        if (result.skipped.length === 0) {
            message.success(t(`websiteContent.list.done.batch.${kind}`, { count: result.succeededIds.length }));
            return;
        }
        Modal.warning({
            title: t('websiteContent.list.batchSkippedTitle', {
                succeeded: result.succeededIds.length,
                skipped: result.skipped.length,
            }),
            content: (
                <ul className="list-disc pl-5 mt-2">
                    {result.skipped.map((skip) => (
                        <li key={skip.contentId}>
                            {titleById.get(skip.contentId) ?? skip.contentId}：
                            {t(`websiteContent.list.skipReason.${kind}.${skip.status ?? 'UNKNOWN'}`, {
                                defaultValue: t(skip.reason, { defaultValue: t('websiteContent.errors.operationFailed') }),
                            })}
                        </li>
                    ))}
                </ul>
            ),
        });
    };

    const runBatch = (kind: 'offline' | 'delete') => {
        const ids = [...selectedIds];
        Modal.confirm({
            title: t(`websiteContent.list.confirm.batch.${kind}`, { count: ids.length }),
            content: kind === 'delete' ? t('websiteContent.list.confirm.deleteHint') : undefined,
            okButtonProps: { danger: kind === 'delete' },
            onOk: async () => {
                try {
                    if (kind === 'offline') {
                        const { data: result } = await batchOffline({ variables: { ids } });
                        if (result) showBatchResult(result.batchTakeWebsiteContentOffline, kind);
                    } else {
                        const { data: result } = await batchDelete({ variables: { ids } });
                        if (result) showBatchResult(result.batchDeleteWebsiteContent, kind);
                    }
                    setSelectedIds([]);
                    await refetch();
                } catch (e) {
                    const text = resolveWebsiteContentError(e, t);
                    if (text) message.error(text);
                }
            },
        });
    };

    const columns: ColumnsType<WebsiteAdminListItem> = [
        {
            title: t('websiteContent.list.columns.cover'),
            key: 'cover',
            width: 96,
            render: (_, item) => <ContentCover url={item.coverThumbnailUrl} />,
        },
        {
            title: t('websiteContent.list.columns.title'),
            key: 'title',
            render: (_, item) => (
                <div className="min-w-0">
                    <Typography.Link
                        className="font-medium"
                        onClick={() => navigate(`/website-content/list/${item.content.id}/edit`)}
                    >
                        {item.title || <span className="text-gray-400">{t('websiteContent.list.untitled')}</span>}
                    </Typography.Link>
                    <div className="text-xs text-gray-400 mt-1">
                        <span className="font-mono">{item.content.slug}</span>
                        <span className="mx-2">·</span>
                        {t(`websiteContent.type.${item.contentType}`)}
                    </div>
                    {item.content.status === 'DRAFT' && item.content.scheduleFailureReason && (
                        <Tooltip title={describeScheduleFailure(item.content.scheduleFailureReason, t)}>
                            <Tag color="red" className="mt-1">{t('websiteContent.list.scheduleFailed')}</Tag>
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: t('websiteContent.list.columns.status'),
            key: 'status',
            width: 130,
            render: (_, item) => <ContentStatusTag content={item.content} />,
        },
        {
            title: t('websiteContent.list.columns.publishTime'),
            key: 'publishTime',
            width: 160,
            render: (_, item) => item.content.status === 'SCHEDULED'
                ? (
                    <Tooltip title={t('websiteContent.list.beijingTime', {
                        time: formatBeijingDateTime(item.content.scheduledAt),
                    })}>
                        {formatPacificDateTime(item.content.scheduledAt)}
                    </Tooltip>
                )
                : formatPacificDate(item.content.displayDate),
        },
        {
            title: t('websiteContent.list.columns.updated'),
            key: 'updated',
            width: 160,
            render: (_, item) => (
                <div>
                    <div>{formatLocalDateTime(item.content.updatedAt)}</div>
                    <div className="text-xs text-gray-400">
                        {item.content.updatedBy ? item.content.updatedByName ?? '-' : t('websiteContent.list.systemOperator')}
                    </div>
                </div>
            ),
        },
        {
            title: t('websiteContent.list.columns.actions'),
            key: 'actions',
            width: 290,
            render: (_, item) => <ContentRowActions item={item} onChanged={() => refetch()} />,
        },
    ];

    const tabItems = [
        { key: ALL_TAB, label: `${t('websiteContent.list.tabs.ALL')} (${totalCount})` },
        ...TAB_STATUSES.map((item) => ({
            key: item,
            label: `${t(`websiteContent.status.${item}`)} (${countOf(item)})`,
        })),
    ];

    return (
        <div className="flex flex-col gap-4 min-h-0">
            <div className="flex items-center justify-between">
                <Typography.Title level={4} className="!mb-0">{t('websiteContent.list.title')}</Typography.Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/website-content/list/new')}>
                    {t('websiteContent.list.create')}
                </Button>
            </div>

            {(list?.scheduleFailedCount ?? 0) > 0 && (
                <Alert
                    type="error"
                    showIcon
                    message={t('websiteContent.list.scheduleFailedBanner', { count: list?.scheduleFailedCount })}
                    action={status !== 'DRAFT' && (
                        <Button size="small" onClick={() => updateParams({ status: 'DRAFT' })}>
                            {t('websiteContent.list.viewDrafts')}
                        </Button>
                    )}
                />
            )}

            <div className="flex flex-wrap items-center gap-3">
                <Input.Search
                    className="!w-96"
                    allowClear
                    maxLength={MAX_KEYWORD_LENGTH}
                    value={keywordInput}
                    placeholder={String(t('websiteContent.list.searchPlaceholder'))}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onSearch={(value) => updateParams({ keyword: normalizeSearchKeyword(value) || null })}
                />
                <Select
                    className="w-40"
                    value={contentType ?? ALL_TAB}
                    onChange={(value) => updateParams({ contentType: value === ALL_TAB ? null : value })}
                    options={[
                        { value: ALL_TAB, label: t('websiteContent.list.allTypes') },
                        ...WEBSITE_CONTENT_TYPES.map((item) => ({ value: item, label: t(`websiteContent.type.${item}`) })),
                    ]}
                />
                <Select
                    className="w-48"
                    value={sortBy}
                    onChange={(value) => updateParams({ sortBy: value === 'UPDATED_AT' ? null : value })}
                    options={[
                        { value: 'UPDATED_AT', label: t('websiteContent.list.sort.UPDATED_AT') },
                        { value: 'PUBLISH_TIME', label: t('websiteContent.list.sort.PUBLISH_TIME') },
                    ]}
                />
            </div>

            <Tabs
                activeKey={status ?? ALL_TAB}
                items={tabItems}
                onChange={(key) => updateParams({ status: key === ALL_TAB ? null : key })}
                className="!mb-0"
            />

            {selectedIds.length > 0 && (
                <Space>
                    <span className="text-gray-500">{t('websiteContent.list.selected', { count: selectedIds.length })}</span>
                    <Button loading={offlining} onClick={() => runBatch('offline')}>
                        {t('websiteContent.list.batchOffline')}
                    </Button>
                    <Button danger loading={deleting} onClick={() => runBatch('delete')}>
                        {t('websiteContent.list.batchDelete')}
                    </Button>
                </Space>
            )}

            <Table<WebsiteAdminListItem>
                rowKey={(item) => item.content.id}
                columns={columns}
                dataSource={list?.items ?? []}
                loading={loading}
                rowClassName={(item) =>
                    (item.content.status === 'DRAFT' && item.content.scheduleFailureReason ? 'bg-red-50' : '')}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: (keys) => setSelectedIds(keys.map(String)),
                }}
                locale={{ emptyText: keyword ? t('websiteContent.list.noMatch') : t('websiteContent.list.empty') }}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: list?.total ?? 0,
                    showSizeChanger: false,
                    showTotal: (total) => t('websiteContent.list.total', { total }),
                    onChange: (next) => updateParams({ page: next > 1 ? String(next) : null }),
                }}
            />
        </div>
    );
};

export default WebsiteContentListPage;
